import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import * as actions from "../../../store/actions/index";
import axios from "../../../axios-url";
// import "./KOTCommon.scss";

// ============================================================================
// KOTDashboard — Kitchen page
// One card per kitchen-station KOT (actiontype === 1). Row tap only moves an
// item between Pending and Preparing. Reaching Ready is a deliberate,
// manual action via the checkbox — never automatic.
// ============================================================================

const toggleKitchenStage = (s) => (s === "pending" ? "preparing" : "pending");

// An item with several modifiers comes back as comma-delimited IDs/qtys in a
// single row, e.g. modifierId: "MOD1,MOD2", modifierQty: "1,2" — split them
// into parallel lists so each modifier can be resolved and shown on its own.
const parseDelimitedList = (value) =>
  value === null || value === undefined || value === ""
    ? []
    : String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

// ⚠️ statusId -> stage mapping. Sample payload has statusId: null, so this
// is a placeholder — replace the numeric keys with your real status-master
// IDs for Pending / Preparing / Ready once confirmed.
const STATUS_ID_TO_STAGE = {
  1: "pending",
  2: "preparing",
  3: "ready",
};

const READY_STATUS_ID = 14;

// The API sends timestamps in UTC but WITHOUT a trailing "Z"/offset, e.g.
// "2026-08-12T12:26:33.47". Per the ES spec, `new Date()` treats a bare
// "T"-time string as LOCAL time, not UTC — so parsing it directly would
// silently shift every timestamp by the browser's UTC offset (+5:30 in
// IST). Force UTC interpretation here so `now - targetTs` in kotUrgency
// (and every display time) is measuring against the right instant.
const parseUtcDate = (dateStr) => {
  if (!dateStr) return null;
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateStr);
  const ts = new Date(hasTimezone ? dateStr : `${dateStr}Z`).getTime();
  return Number.isNaN(ts) ? null : ts;
};

const formatApiTime = (dateStr) => {
  const ts = parseUtcDate(dateStr);
  if (ts === null) return "";
  return new Date(ts).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── KOT ageing → header colour ─────────────────────────────────────────────
// Minutes elapsed past an item's target time. Under the warn threshold the
// header keeps its default navy; nothing else on the card changes colour.
const KOT_WARN_MINUTES = 5;
const KOT_LATE_MINUTES = 10;

// TARGETDATE is stamped when the action row is raised (RestaurantOrder sends
// requestDate into it), so it is the KOT's own clock. createddate is the
// fallback for rows that predate it.
const itemTargetTime = (item) => parseUtcDate(item.targetdate || item.createddate);

// A KOT is only as fresh as its OLDEST item — that item has been waiting the
// longest, so its target time drives the colour for the whole card.
const earliestTargetTime = (items) =>
  items.reduce((earliest, it) => {
    const ts = itemTargetTime(it);
    if (ts === null) return earliest;
    return earliest === null || ts < earliest ? ts : earliest;
  }, null);

const kotUrgency = (items, now) => {
  const targetTs = earliestTargetTime(items);
  if (targetTs === null) return "";

  const elapsedMinutes = (now - targetTs) / 60000;

  if (elapsedMinutes > KOT_LATE_MINUTES) return "late";
  if (elapsedMinutes > KOT_WARN_MINUTES) return "warn";
  return "";
};

// One card PER KITCHEN STATION KOT. Different cuisines (e.g. South Indian
// vs Chinese) are prepared by different kitchen counters even when they
// belong to the same order — so they must appear as SEPARATE KOT cards.
const kotStationKey = (r) =>
  r.kotnumber || `${r.requestid}-${r.menuitemkitchenid || r.kitchenid || "default"}`;

// One row from GetresActionAllList -> one item. This is the SINGLE source of
// truth this page works off.
const mapActionsToItems = (records, menuItems, modifierItems = []) => {
  if (!Array.isArray(records)) return [];

  return records
    .filter((r) => r.menuId !== null && r.menuId !== undefined)
    .map((r) => {
      const menu = menuItems.find(
        (x) =>
          String(x.value) === String(r.menuId) ||
          String(x.id) === String(r.menuId) ||
          String(x.menuId) === String(r.menuId)
      );

      const isReady =
        r.statusId !== null &&
        r.statusId !== undefined &&
        Number(r.statusId) === READY_STATUS_ID;

      const modifierIds = parseDelimitedList(r.modifierId);
      const modifierQtys = parseDelimitedList(r.modifierQty);
      const modifiers = modifierIds.map((modId, idx) => {
        const modifier = modifierItems.find(
          (x) => String(x.value) === String(modId) || String(x.id) === String(modId)
        );
        return {
          id: modId,
          name: modifier?.label || modifier?.name || modifier?.text || `Modifier ${modId}`,
          qty: modifierQtys[idx] ?? modifierQtys[0] ?? null,
        };
      });

      return {
        ...r,
        id: r.requestactionid,
        requestId: r.requestid,
        kotKey: kotStationKey(r),
        kotNo: r.kotnumber,
        tableNo: r.tablenumber,
        tokenNo: r.tokennumber,
        createdTime: formatApiTime(r.createddate),
        actiontype: r.actiontype,
        menuId: r.menuId,
        name: menu?.label || menu?.name || menu?.menuName || menu?.text || `Menu ${r.menuId}`,
        qty: r.menuQty || 1,
        modifiers,
        status: isReady ? "ready" : STATUS_ID_TO_STAGE[r.statusId] || "pending",
        statusId: r.statusId,
        // selected: isReady,
        selected: false,
        hidden: false,
      };
    });
};

// Group by kitchen-station KOT. actiontype === 1 -> Kitchen.
const groupItemsByKot = (items) => {
  // const filtered = items.filter((it) => it.actiontype === 1);
  const filtered = items.filter(
    (it) => it.actiontype === 1 && !it.hidden
  );
  const grouped = filtered.reduce((acc, it) => {
    if (!acc[it.kotKey]) {
      acc[it.kotKey] = {
        key: it.kotKey,
        groupType: "kot",
        kotNo: it.kotNo,
        tableNo: it.tableNo,
        tokenNo: it.tokenNo,
        actiontype: it.actiontype,
        createdTime: it.createdTime,
        items: [],
      };
    }
    acc[it.kotKey].items.push(it);
    return acc;
  }, {});
  return Object.values(grouped);
};

// Groups the summary by menu item. Falls back to the name when menuId is
// missing so two unnamed rows never collapse into one another.
const menuSummaryKey = (item) => String(item.menuId ?? item.name);

// Roll every outstanding item across ALL visible KOTs up into one line per menu
// item. This is what the KDS operator reads to know "how many dosas do I need
// to fire right now" without scanning each card.
//
// Each row keeps a reference to the ORIGINAL item objects it was built from —
// the expanded child rows and their checkboxes drive exactly the same
// `selected` flag the KOT cards do. No parallel state.
const buildMenuSummary = (groups) => {
  const summary = groups.reduce((acc, group) => {
    group.items.forEach((it) => {
      if (it.status === "ready") return;

      const key = menuSummaryKey(it);
      const qty = Number(it.qty) || 0;

      if (!acc[key]) {
        acc[key] = {
          key,
          name: it.name,
          qty: 0,
          pendingQty: 0,
          preparingQty: 0,
          kots: new Set(),
          items: [],
        };
      }

      acc[key].qty += qty;
      if (it.status === "preparing") acc[key].preparingQty += qty;
      else acc[key].pendingQty += qty;
      acc[key].kots.add(group.key);
      acc[key].items.push(it);
    });
    return acc;
  }, {});

  return Object.values(summary)
    .map(({ kots, ...row }) => ({ ...row, kotCount: kots.size }))
    .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
};

class KOTDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      currentTime: this._formatTime(new Date()),
      // Drives the ageing colour on each KOT header. Kept in state so the
      // re-render is an explicit consequence of the clock, not a side effect.
      now: Date.now(),
      // Summary-panel rows the user has drilled into, keyed by menuSummaryKey.
      expandedMenus: {},
      // Drives the spin animation / disabled state on the Refresh button.
      refreshing: false,
    };
  }

  componentDidMount() {
    // 15s keeps the 5/10 minute header colour changes tight enough to trust.
    this._clockInterval = setInterval(() => {
      this.setState({ currentTime: this._formatTime(new Date()), now: Date.now() });
    }, 15000);

    this.props.loadKotActions();
    this._stopKotLive = this.props.startKotActionsLive();
    this.props.getMasterKeywordList("MenuItem");
    this.props.getMasterKeywordList("Modifier");
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tableLists !== this.props.tableLists && this.state.refreshing) {
      this.setState({ refreshing: false });
    }

    if (
      prevProps.tableLists !== this.props.tableLists ||
      prevProps.MenuItem !== this.props.MenuItem ||
      prevProps.Modifier !== this.props.Modifier
    ) {
      if (!Array.isArray(this.props.tableLists) || this.props.tableLists.length === 0) {
        return;
      }

      const mappedItems = mapActionsToItems(
        this.props.tableLists,
        this.props.MenuItem,
        this.props.Modifier
      );

      // this.setState({ items: mappedItems }, () => {
      //   this.removeReadyKotsAfterReload(mappedItems);
      // });
      this.setState(
        {
          items: mappedItems
        },
        () => {
          this.removeReadyKotsAfterReload();
        }
      );
    }
  }

  componentWillUnmount() {
    clearInterval(this._clockInterval);
    if (this._stopKotLive) this._stopKotLive();
  }

  _formatTime(d) {
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }

  // Tapping the row body cycles Pending ↔ Preparing only.
  handleItemStatusChange = (itemId) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        if (it.id !== itemId || it.status === "ready") return it;
        const nextStatus = toggleKitchenStage(it.status);
        // TODO: persist statusId back to the API, e.g.
        // this.props.updateRequestActionStatus(it.id, nextStatus);
        return { ...it, status: nextStatus };
      }),
    }));
  };

  handleSelectAll = (groupType, groupKey, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        const matches = groupType === "kot" ? it.kotKey === groupKey : it.requestId === groupKey;
        if (!matches) return it;
        return { ...it, selected: it.status === "ready" ? false : checked };
      }),
    }));
  };

  handleReadyToggle = (itemId, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => (it.id !== itemId ? it : { ...it, selected: checked })),
    }));
  };

  // Re-pulls the whole KOT list from the API; every call is a fresh hit.
  handleRefresh = () => {
    if (this.state.refreshing) return;
    this.setState({ refreshing: true });
    this.props.loadKotActions();
  };

  toggleSummaryExpand = (menuKey) => {
    this.setState((prev) => ({
      expandedMenus: {
        ...prev.expandedMenus,
        [menuKey]: !prev.expandedMenus[menuKey],
      },
    }));
  };

  // Menu-item-level select in the summary panel: ticks every outstanding line
  // of that menu item across every KOT. Writes the same `selected` flag the
  // KOT cards read, so both presentations stay in lock-step.
  handleSummarySelectMenu = (menuKey, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        if (it.actiontype !== 1 || it.hidden || it.status === "ready") return it;
        if (menuSummaryKey(it) !== menuKey) return it;
        return { ...it, selected: checked };
      }),
    }));
  };

  // Select / clear every outstanding item across ALL visible KOTs at once, so
  // the common Mark Ready button can clear the whole queue in two taps.
  handleSelectAllVisible = (checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        if (it.actiontype !== 1 || it.hidden) return it;
        return { ...it, selected: it.status === "ready" ? false : checked };
      }),
    }));
  };

  // The single, page-level Mark Ready action. Replaces the per-KOT buttons —
  // whatever is ticked anywhere on the board goes ready in one call.
  handleGlobalMarkReady = () => {
    const selectedItems = this.state.items.filter(
      (it) => it.actiontype === 1 && it.selected && it.status !== "ready"
    );

    this.markItemsReady(selectedItems);
  };

  // Marks an arbitrary set of items ready. The set may span several KOTs, so
  // every KOT it touches is re-checked for completion afterwards.
  markItemsReady = async (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;

    const affectedKots = new Set(selectedItems.map((it) => it.kotKey));
    const selectedIds = new Set(selectedItems.map((it) => it.id));

    try {
      // const data = selectedItems.map((item) => {
      //   const {
      //     id,
      //     requestId,
      //     kotKey,
      //     kotNo,
      //     tableNo,
      //     tokenNo,
      //     createdTime,
      //     name,
      //     qty,
      //     status,
      //     selected,
      //     ...backendData
      //   } = item;

      //   return {
      //     ...backendData,
      //     // update only status
      //     statusId: READY_STATUS_ID,
      //   };
      // });
      // const TAKEAWAY = "550e8400-e29b-41d4-a716-446655440002";
      // const DELIVERY = "550e8400-e29b-41d4-a716-446655440003";
      // const data = selectedItems.map((item) => {
      //   const {
      //     id,
      //     requestId,
      //     kotKey,
      //     kotNo,
      //     tableNo,
      //     tokenNo,
      //     createdTime,
      //     name,
      //     qty,
      //     status,
      //     selected,
      //     ...backendData
      //   } = item;

      //   if (
      //     backendData.ordertype === TAKEAWAY ||
      //     backendData.ordertype === DELIVERY
      //   ) {
      //     return {
      //       ...backendData,
      //       actiontype: 2,
      //       actiondescription: "Packing",
      //       statusId: 21,
      //       targetdate: new Date().toISOString(),

      //     };
      //   }

      //   return {
      //     ...backendData,
      //     statusId: READY_STATUS_ID,
      //   };
      // });

      const TAKEAWAY = "550e8400-e29b-41d4-a716-446655440002";
      const DELIVERY = "550e8400-e29b-41d4-a716-446655440003";

      const data = [];

      selectedItems.forEach((item) => {
        const {
          id,
          requestId,
          kotKey,
          kotNo,
          tableNo,
          tokenNo,
          createdTime,
          name,
          qty,
          status,
          selected,
          ...backendData
        } = item;

        // First object -> Dine In (Kitchen Ready)
        data.push({
          ...backendData,
          statusId: READY_STATUS_ID,
        });

        // Second object -> Packing
        if (
          backendData.ordertype === TAKEAWAY ||
          backendData.ordertype === DELIVERY
        ) {
          // data.push({
          //   ...backendData,
          //   requestactionid:null,
          //   actiontype: 2,
          //   actiondescription: "Packing",
          //   statusId: 21,
          //   targetdate: new Date().toISOString(),
          // });
          const { requestactionid, ...packingData } = backendData;

          data.push({
            ...packingData,
            actiontype: 2,
            actiondescription: "Packing",
            statusId: 21,
            targetdate: new Date().toISOString(),
          });
        }
      });

      await axios.post("/master/api/UpdateDynamicData?model=Restaurant_Action_Update", {
        Key: JSON.stringify(data),
      });

      this.setState(
        (prev) => ({
          items: prev.items.map((it) => {
            if (!selectedIds.has(it.id)) return it;

            return {
              ...it,
              selected: false,
              hidden: false,
              status: "ready",
              statusId: READY_STATUS_ID,
            };
          }),
        }),
        () => {

          setTimeout(() => {

            this.setState((prev) => {

              const updatedItems = prev.items.map((it) =>
                selectedIds.has(it.id) ? { ...it, hidden: false } : it
              );


              // Drop every KOT this action touched that is now fully ready.
            //   const completedKots = new Set();

            //   affectedKots.forEach((key) => {

            //     const kotItems = updatedItems.filter(
            //       x => x.kotKey === key
            //     );

            //     const allReady =
            //       kotItems.length > 0 &&
            //       kotItems.every(
            //         x => Number(x.statusId) === READY_STATUS_ID
            //       );

            //     if (allReady) {
            //       completedKots.add(key);
            //     }

            //   });


            //   if (completedKots.size > 0) {

            //     return {
            //       items: updatedItems.filter(
            //         x => !completedKots.has(x.kotKey)
            //       )
            //     }

            //   }


            //   return {
            //     items: updatedItems
            //   };

            // });
             const completedKots = new Set();

  affectedKots.forEach((kotKey) => {
    const kotItems = updatedItems.filter(
      (item) => item.kotKey === kotKey
    );

    const allReady =
      kotItems.length > 0 &&
      kotItems.every(
        (item) => Number(item.statusId) === READY_STATUS_ID
      );

    if (allReady) {
      completedKots.add(kotKey);
    }
  });

  // Remove only KOTs where every item is Ready
  return {
    items: updatedItems.filter(
      (item) => !completedKots.has(item.kotKey)
    ),
  };
});


          });

        }
      );
    } catch (err) {
      console.log(err);
    }
  };

  removeCompletedKot = (groupType, groupKey) => {
    this.setState((prev) => {
      // Get all items in this KOT
      const groupItems = prev.items.filter((item) =>
        groupType === "kot"
          ? item.kotKey === groupKey
          : item.requestId === groupKey
      );

      // Remove the KOT only if every item is hidden
      const allHidden = groupItems.every((item) => item.hidden);

      if (!allHidden) {
        return null;
      }

      return {
        items: prev.items.filter((item) =>
          groupType === "kot"
            ? item.kotKey !== groupKey
            : item.requestId !== groupKey
        ),
      };
    });
  };

  // removeCompletedKot = (groupType, groupKey) => {
  //   this.setState((prev) => {
  //     // Hide the entire KOT only if no visible items remain.
  //     const visibleItems = prev.items.filter((item) => {
  //       const matches =
  //         groupType === "kot"
  //           ? item.kotKey === groupKey
  //           : item.requestId === groupKey;

  //       return matches && !item.hidden;
  //     });

  //     if (visibleItems.length > 0) {
  //       return null; // Some items are still pending, so keep the KOT.
  //     }

  //     return {
  //       items: prev.items.filter((item) =>
  //         groupType === "kot"
  //           ? item.kotKey !== groupKey
  //           : item.requestId !== groupKey
  //       ),
  //     };
  //   });
  // };

  // removeCompletedKot = (groupType, groupKey) => {
  //   const groupItems = this.state.items.filter((item) =>
  //     groupType === "kot" ? item.kotKey === groupKey : item.requestId === groupKey
  //   );

  //   const allReady = groupItems.every((item) => item.status === "ready");
  //   if (!allReady) return;

  //   setTimeout(() => {
  //     this.setState((prev) => ({
  //       items: prev.items.filter((item) =>
  //         groupType === "kot" ? item.kotKey !== groupKey : item.requestId !== groupKey
  //       ),
  //     }));
  //   });
  // };

  // removeReadyKotsAfterReload = (items) => {
  //   const readyGroups = {};

  //   items.forEach((item) => {
  //     if (Number(item.statusId) === READY_STATUS_ID) {
  //       if (!readyGroups[item.kotKey]) readyGroups[item.kotKey] = [];
  //       readyGroups[item.kotKey].push(item);
  //     }
  //   });

  //   Object.keys(readyGroups).forEach((key) => {
  //     const groupItems = items.filter((item) => item.kotKey === key);
  //     const allReady = groupItems.every((item) => Number(item.statusId) === READY_STATUS_ID);

  //     if (allReady) {
  //       setTimeout(() => {
  //         this.setState((prev) => ({
  //           items: prev.items.filter((item) => item.kotKey !== key),
  //         }));
  //       });
  //     }
  //   });
  // };

  // removeReadyKotsAfterReload = () => {
  //   this.setState((prev) => {
  //     const items = prev.items;

  //     const completedKots = new Set();

  //     const groups = {};

  //     items.forEach((item) => {
  //       if (!groups[item.kotKey]) {
  //         groups[item.kotKey] = [];
  //       }
  //       groups[item.kotKey].push(item);
  //     });

  //     Object.keys(groups).forEach((key) => {
  //       const allReady = groups[key].every(
  //         (item) => Number(item.statusId) === READY_STATUS_ID
  //       );

  //       if (allReady) {
  //         completedKots.add(key);
  //       }
  //     });

  //     if (completedKots.size === 0) {
  //       return null;
  //     }

  //     return {
  //       items: items.filter((item) => !completedKots.has(item.kotKey)),
  //     };
  //   });
  // };

  // removeReadyKotsAfterReload = () => {

  //   this.setState((prev) => ({

  //     items: prev.items.map(item => {

  //       if (Number(item.statusId) === READY_STATUS_ID) {

  //         return {
  //           ...item,
  //           hidden: true,
  //           status: "ready"
  //         }

  //       }

  //       return item;

  //     })

  //   }), () => {


  //     setTimeout(() => {

  //       this.setState((prev) => {


  //         const itemsAfterShow = prev.items.map(item => ({

  //           ...item,
  //           hidden: false

  //         }));


  //         const groups = {};


  //         itemsAfterShow.forEach(item => {

  //           if (!groups[item.kotKey]) {
  //             groups[item.kotKey] = [];
  //           }

  //           groups[item.kotKey].push(item);

  //         });



  //         const completedKeys = Object.keys(groups)
  //           .filter(key =>

  //             groups[key].every(
  //               item => Number(item.statusId) === READY_STATUS_ID
  //             )

  //           );


  //         return {

  //           items:
  //             completedKeys.length

  //               ? itemsAfterShow.filter(
  //                 item => !completedKeys.includes(item.kotKey)
  //               )

  //               : itemsAfterShow

  //         }


  //       })


  //     })


  //   })

  // }

  removeReadyKotsAfterReload = () => {
  this.setState((prev) => {
    const kotGroups = {};

    // Group only Kitchen items by KOT
    prev.items.forEach((item) => {
      if (item.actiontype !== 1) return;

      if (!kotGroups[item.kotKey]) {
        kotGroups[item.kotKey] = [];
      }

      kotGroups[item.kotKey].push(item);
    });

    // Find KOTs where ALL items are Ready
    const completedKots = new Set();

    Object.keys(kotGroups).forEach((kotKey) => {
      const kotItems = kotGroups[kotKey];

      const allReady =
        kotItems.length > 0 &&
        kotItems.every(
          (item) => Number(item.statusId) === READY_STATUS_ID
        );

      if (allReady) {
        completedKots.add(kotKey);
      }
    });

    // Remove ONLY completed KOTs
    if (completedKots.size === 0) {
      return null;
    }

    return {
      items: prev.items.filter(
        (item) => !completedKots.has(item.kotKey)
      ),
    };
  });
};
  renderMenuSummary = (summaryRows, totalQty, kotCount) => (
    <aside className="kot-summary-panel">
      <div className="kot-summary-panel__header">
        <div className="kot-summary-panel__title-row">
          <i className="pi pi-list kot-summary-panel__icon" />
          <h3 className="kot-summary-panel__title">Item Summary</h3>
        </div>
        <span className="kot-summary-panel__subtitle">
          {totalQty} item{totalQty === 1 ? "" : "s"} &middot; {kotCount} KOT
          {kotCount === 1 ? "" : "s"}
        </span>
      </div>

      {summaryRows.length === 0 ? (
        <div className="kot-summary-empty">
          <i className="pi pi-check-circle kot-summary-empty__icon" />
          <span>Queue is clear</span>
        </div>
      ) : (
        <div className="kot-summary-list">
          {summaryRows.map(this.renderSummaryGroup)}
        </div>
      )}
    </aside>
  );

  // One menu-item line + its (collapsed by default) per-order breakdown.
  renderSummaryGroup = (row) => {
    const expanded = !!this.state.expandedMenus[row.key];

    // row.items only ever holds outstanding lines, so "all" means all of them.
    const selectedCount = row.items.filter((it) => it.selected).length;
    const allSelected = selectedCount === row.items.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <div key={row.key} className="kot-summary-group">
        <div
          className={`kot-summary-row ${someSelected ? "kot-summary-row--partial" : ""}`}
        >
          <Checkbox
            checked={allSelected}
            onChange={(e) => this.handleSummarySelectMenu(row.key, e.checked)}
            className="kot-ready-check kot-summary-check"
          />

          <span className="kot-summary-qty">{row.qty}</span>

          <div
            className="kot-summary-info"
            onClick={() => this.toggleSummaryExpand(row.key)}
          >
            <span className="kot-summary-name">{row.name}</span>
            <span className="kot-summary-meta">
              {row.kotCount} KOT{row.kotCount === 1 ? "" : "s"}
              {row.preparingQty > 0 && (
                <>
                  <span className="dot">&middot;</span>
                  {row.preparingQty} preparing
                </>
              )}
              {selectedCount > 0 && (
                <>
                  <span className="dot">&middot;</span>
                  <span className="kot-summary-meta--selected">
                    {selectedCount} selected
                  </span>
                </>
              )}
            </span>
          </div>

          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse breakdown" : "Expand breakdown"}
            className="kot-summary-expand"
            onClick={() => this.toggleSummaryExpand(row.key)}
          >
            <i className={`pi ${expanded ? "pi-chevron-up" : "pi-chevron-down"}`} />
          </button>
        </div>

        {expanded && (
          <div className="kot-summary-children">
            {row.items.map((it) => (
              <div key={it.id} className="kot-summary-child">
                <Checkbox
                  checked={it.selected || false}
                  onChange={(e) => this.handleReadyToggle(it.id, e.checked)}
                  className="kot-ready-check kot-summary-check"
                />

                <span className="kot-summary-child-qty">&times;{it.qty}</span>

                <div className="kot-summary-child-info">
                  <span className="kot-summary-child-kot">{it.kotNo}</span>
                  <span className="kot-summary-child-meta">
                    {it.tableNo ? `Table ${it.tableNo}` : `Token ${it.tokenNo}`}
                    <span className="dot">&middot;</span>
                    <span
                      className={`kot-summary-child-stage kot-summary-child-stage--${it.status}`}
                    >
                      {it.status === "preparing" ? "Preparing" : "Pending"}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderKOTCard = (group) => {
    const allReady = group.items.every((i) => i.status === "ready");
    const selectedCount = group.items.filter(
      (i) => i.selected && i.status !== "ready"
    ).length;
    const urgency = kotUrgency(group.items, this.state.now);

    return (
      <div key={group.key} className="kot-card">
        <div
          className={`kot-card__header ${urgency ? `kot-card__header--${urgency}` : ""}`}
        >
          <div className="kot-card__header-top">
            <span className="kot-card__kot-no">{group.kotNo}</span>
            <small className="kot-card__time">
              <i className="pi pi-clock p-mr-1" />
              {group.createdTime}
            </small>
          </div>

          <div className="kot-card__meta">
            {group.tableNo && (
              <>
                Table {group.tableNo} <span className="dot">&middot;</span>
              </>
            )}
            Token {group.tokenNo}
            <span className="dot">&middot;</span>
            Dine-in
          </div>
        </div>

        <div className="kot-card__items">
          <div className="kot-select-all">
            <span className="kot-select-all-label">Select All</span>

            <Checkbox
              checked={
                group.items.filter((item) => item.status !== "ready").length > 0 &&
                group.items
                  .filter((item) => item.status !== "ready")
                  .every((item) => item.selected)
              }
              onChange={(e) => this.handleSelectAll(group.groupType, group.key, e.checked)}
              className="kot-select-all-checkbox"
            />
          </div>

          {group.items.map((item) => {
            const isDone = item.status === "ready";
            const isSelected = item.selected || false;

            return (
              <div
                key={item.id}
                className={`kot-item-row ${isDone ? "kot-item-row--done" : ""}`}
                onClick={() => !isDone && this.handleItemStatusChange(item.id)}
              >
                <span className="kot-item-qty">&times;{item.qty}</span>

                <span className="kot-item-info">
                  <span className={`kot-item-name ${isDone ? "kot-item-name--done" : ""}`}>
                    {item.name}
                  </span>
                  {item.modifiers.map((mod) => (
                    <span key={mod.id} className="kot-item-modifier">
                      {mod.name}
                      {mod.qty ? ` ×${mod.qty}` : ""}
                    </span>
                  ))}
                </span>

                 <span
                  className={`kot-ready-pill ${isDone ? "kot-ready-pill--active" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className={`kot-ready-label ${isDone ? "kot-ready-label--active" : ""}`}>
                    Ready
                  </span>
                  <Checkbox
                    checked={isDone || isSelected}
                    disabled={isDone}
                    onChange={(e) => this.handleReadyToggle(item.id, e.checked)}
                    className="kot-ready-check"
                  />
                </span>
              </div>
            );
          })}
        </div>

        <div className="kot-card__footer">
          {allReady ? (
            <span className="kot-all-ready-badge">
              <i className="pi pi-check-circle" />
              All ready
            </span>
          ) : (
            <span
              className={`kot-card__selection ${selectedCount > 0 ? "kot-card__selection--active" : ""
                }`}
            >
              {selectedCount > 0
                ? `${selectedCount} of ${group.items.length} selected`
                : "Tick items, then Mark Ready"}
            </span>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { items, currentTime } = this.state;
    const kitchenGroups = groupItemsByKot(items);
    const summaryRows = buildMenuSummary(kitchenGroups);
    const summaryTotalQty = summaryRows.reduce((sum, row) => sum + row.qty, 0);

    const selectableItems = kitchenGroups.reduce(
      (acc, group) => acc.concat(group.items.filter((it) => it.status !== "ready")),
      []
    );
    const selectedCount = selectableItems.filter((it) => it.selected).length;
    const allSelected =
      selectableItems.length > 0 && selectedCount === selectableItems.length;

    return (
      <div className="kot-dashboard-root kot-dashboard-root--split">
        {this.props.loading && <div className="page-loader"></div>}
        {this.props.error !== null && this.props.error !== undefined && (
          <div className="p-error" style={{ padding: "0.5em 1em" }}>
            {this.props.error}
          </div>
        )}

        <div className="kot-page-header">
          <div className="kot-page-header__brand">
            <div className="kot-page-header__title-row">
              <i className="pi pi-bolt kot-page-header__icon" />
              <h2 className="kot-page-header__title">Kitchen</h2>
              <span className="kot-tab-count kot-tab-count--active">{kitchenGroups.length}</span>
            </div>
          </div>
          <div className="kot-page-header__actions">
            <small className="kot-page-header__time">
              <i className="pi pi-clock p-mr-1" />
              {currentTime}
            </small>

            {selectableItems.length > 0 && (
              <span className="kot-global-select-all">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => this.handleSelectAllVisible(e.checked)}
                  className="kot-ready-check"
                />
                <span onClick={() => this.handleSelectAllVisible(!allSelected)}>
                  Select all
                </span>
              </span>
            )}

            <Button
              icon={`pi pi-refresh ${this.state.refreshing ? "kot-refresh-btn__icon--spin" : ""}`}
              label={this.state.refreshing ? "Refreshing" : "Refresh"}
              className="p-button-sm kot-refresh-btn"
              onClick={this.handleRefresh}
              disabled={this.state.refreshing}
              aria-label="Refresh"
              tooltip="Refresh KOT data"
              tooltipOptions={{ position: "bottom" }}
            />

            <Button
              label={selectedCount > 0 ? `Mark Ready (${selectedCount})` : "Mark Ready"}
              icon="pi pi-check"
              disabled={selectedCount === 0}
              className="p-button-sm kot-mark-ready-btn kot-mark-ready-btn--global"
              onClick={this.handleGlobalMarkReady}
            />
          </div>
        </div>

        <div className="kot-dashboard-body">
          <div className="kot-dashboard-main">
            {kitchenGroups.length === 0 ? (
              <div className="kot-empty-state">
                <i className="pi pi-inbox kot-empty-state__icon" />
                <span className="p-text-secondary">No active Kitchen KOTs</span>
              </div>
            ) : (
              <div className="kot-card-grid">{kitchenGroups.map(this.renderKOTCard)}</div>
            )}
          </div>

          {this.renderMenuSummary(summaryRows, summaryTotalQty, kitchenGroups.length)}
        </div>
      </div>
    );
  }
}

// ─── Redux wiring ─────────────────────────────────────────────────────────────
// Mirrors Property.jsx: getMasterTableList('res/GetPropertyList', Companyid)
// -> here we call getMasterTableList('res/GetresActionAllList', Companyid),
// which hits: /api/res/GetresActionAllList?companyid=<Companyid>
const mapStateToProps = (state) => ({
  loading: state.master.loading,
  error: state.master.error,
  success: state.master.success,
  // The action list has its own store slot, kept live by startKotActionsLive.
  tableLists: state.master.kotActions,
  menuItems: state.keyword.keywordList,
  MenuItem: state.keyword.MenuItem !== undefined ? state.keyword.MenuItem : [],
  Modifier: state.keyword.Modifier !== undefined ? state.keyword.Modifier : [],
});

const mapDispatchToProps = (dispatch) => ({
  getMasterTableList: (table, company) => dispatch(actions.getMasterTableList(table, company)),
  loadKotActions: () => dispatch(actions.loadKotActions()),
  startKotActionsLive: () => dispatch(actions.startKotActionsLive()),
  getMasterKeywordList: (keyword) => dispatch(actions.getMasterKeywordList(keyword)),
  // TODO: once you have a real "update status" endpoint, wire it here.
});

export default connect(mapStateToProps, mapDispatchToProps)(KOTDashboard);